import loadScript from '../helpers/scriptLoader';
import countryData from '../../config/countries.yaml';
import { rateLimitedfetch } from '../helpers/requestLimiter';
import config, { LOCATION_SESSION_STORAGE_KEY } from '../../config/config';

const { LOQATE_FIND_URL, LOQATE_RETRIEVE_URL, LOQATE_KEY } = config;
const LIMIT = 1000;

const geocodeScriptSrc = 'https://js.maxmind.com/js/apis/geoip2/v2.1/geoip2.js';

export const getCountry = () => {
  return new Promise(async (resolve, reject) => {
    /*
     * First check if we already have it in session storage
     */
    let country = sessionStorage.getItem(LOCATION_SESSION_STORAGE_KEY) || null;

    if (country) {
      // We may have session storage without the new 3 char iso code, so populate it
      const parsedCountry = JSON.parse(country);

      if (parsedCountry.isoCode3) {
        return resolve(parsedCountry);
      }

      const isoCode3 = countryData.find(c => c.isoCode === parsedCountry.country)?.isoCode3;

      return resolve({ ...parsedCountry, isoCode3 });
    }

    /*
     * We don't already have the user's location so call the service
     *
     * First we need to load the GeoIp script - we don't need it all
     * the time, so lazyloading it
     */
    try {
      await loadScript(geocodeScriptSrc);
    } catch (err) {
      return reject(err);
    }

    /*
     * GeoIp2 will now be available
     */

    /* eslint-disable */
    geoip2.country(res => {
      /* eslint-enable */
      /*
       * Double check it is a location we recognise
       */
      let defaultCountryData = countryData.find(location => location.isoCode === res.country.iso_code);

      if (!defaultCountryData) {
        return reject(new Error(`Could not identify country with isocode ${res.country.iso_code}`));
      }

      let { isoCode: country, currency, isoCode3 } = defaultCountryData;

      const locatedCountry = {
        country,
        currency,
        isoCode3
      };

      sessionStorage.setItem(LOCATION_SESSION_STORAGE_KEY, JSON.stringify(locatedCountry));

      resolve(locatedCountry);
    }, reject);
  });
};

const findLocation = async data => {
  data = {
    Key: LOQATE_KEY,
    IsMiddleware: false,
    Language: 'en-gb',
    ...data
  };

  const queryString = Object.keys(data)
    .map(key => `${key}=${data[key]}`)
    .join('&');

  const response = await rateLimitedfetch(`${LOQATE_FIND_URL}?${queryString}`, {
    method: 'POST'
  });
  const locationData = await response.json();

  let error = locationData.Items.find(item => 'Error' in item);
  if (error) {
    throw new Error(error);
  }

  return locationData;
};

const retrieveLocation = async id => {
  let data = {
    Key: LOQATE_KEY,
    Id: id
  };

  const queryString = Object.keys(data)
    .map(key => `${key}=${data[key]}`)
    .join('&');

  const response = await rateLimitedfetch(`${LOQATE_RETRIEVE_URL}?${queryString}`, {
    method: 'POST'
  });
  const locationData = await response.json();

  let error = locationData.Items.find(item => 'Error' in item);
  if (error) {
    throw new Error(error);
  }

  return locationData;
};

export const getLocation = async (country, postalCode, postalCodeSeparator, validator) => {
  let locationError = new Error(`Could not get location data for ${postalCode} ${country}`);

  /*
   * Lookup postcode
   */
  let locationsFromPostalCode = await findLocation({
    Origin: country,
    Countries: [country],
    Limit: LIMIT,
    Text: postalCode
  });

  /*
   * Need to find postcode in the results
   */
  let locationData = locationsFromPostalCode.Items.find((locationData, i) => {
    const { Type, Text: code, Description } = locationData;
    return (
      (Type === 'Postcode' && areEqualThePostalCodes(postalCode, code, postalCodeSeparator)) ||
      (Type === 'Address' &&
        areEqualThePostalCodes(postalCode, Description.match(RegExp(validator, 'g'))[0], postalCodeSeparator))
    );
  });

  if (!locationData) {
    throw locationError;
  }

  if (locationData.Type === 'Postcode') {
    /**
     * WE NEED TO FIND A LOCATION OF TYPE=Address
     * We need to pass the `locationData` id back as the "Container" field and then do
     * another lookup with the same postal code
     */
    locationsFromPostalCode = await findLocation({
      Container: locationData.Id,
      Limit: 1,
      Text: postalCode
    });

    if (locationsFromPostalCode.Items.length === 0) {
      throw locationError;
    }

    locationData = locationsFromPostalCode.Items[0];
  }

  /*
   * Now we have an address, we need to send it over to the retrieve service
   */
  const locationRetrieved = await retrieveLocation(locationData.Id);

  if (locationRetrieved.Items.length === 0) {
    throw locationError;
  }

  return locationRetrieved.Items[0];
};

function areEqualThePostalCodes(originalCode, codeFromApiCall, postalCodeSeparator) {
  if (postalCodeSeparator) {
    const regex = new RegExp(postalCodeSeparator, 'gi');
    return originalCode.replace(regex, '') === codeFromApiCall.replace(regex, '');
  }
  return originalCode === codeFromApiCall;
}
